import type { TraderProfile } from "./onboarding-v2";

interface AuthedRequestInput {
  apiBase: string;
  getAccessToken: () => Promise<string | null>;
}

export interface DeskChoiceResult {
  deskId: string;
  deskName: string;
  invitationId?: string;
  notificationPushed?: number;
  softBypass?: boolean;
}

const SOFT_BYPASS_DESK: DeskChoiceResult = {
  deskId: "no-desk",
  deskName: "No desk",
  softBypass: true,
};

export async function requestPricedInCapitalInvite(
  input: AuthedRequestInput & {
    profile: TraderProfile;
    userEmail?: string | null;
  },
): Promise<DeskChoiceResult> {
  try {
    const json = await postJson<{
      item?: { id: string };
      notification?: { pushed?: number };
    }>(input, "/api/desk-inbox/invitations", {
      brokerOrPropFirm: input.profile.brokerOrPropFirm,
      deskId: "priced-in-capital",
      deskName: "Priced In Capital",
      requesterEmail: input.userEmail,
      requesterName: input.profile.nameTag,
    });
    return {
      deskId: "priced-in-capital",
      deskName: "Priced In Capital",
      invitationId: json.item?.id,
      notificationPushed: json.notification?.pushed,
    };
  } catch (error) {
    if (isDeskMissingError(error)) return SOFT_BYPASS_DESK;
    throw error;
  }
}

export async function createOnboardingDesk(
  input: AuthedRequestInput & {
    name: string;
  },
): Promise<DeskChoiceResult> {
  const json = await postJson<{ desk: DeskChoiceResult }>(
    input,
    "/api/desk-inbox/desks",
    { name: input.name },
  );
  return json.desk;
}

async function postJson<T>(
  input: AuthedRequestInput,
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const token = await input.getAccessToken();
  const res = await fetch(`${input.apiBase.replace(/\/$/, "")}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    const error = new Error(data.error ?? "Desk setup failed.");
    Object.assign(error, { status: res.status });
    throw error;
  }
  return data;
}

function isDeskMissingError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const status = (error as Error & { status?: number }).status;
  return status === 404 || /desk.+not found|not found/i.test(error.message);
}
